import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

const PendingEventsPage = () => {
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [editForm, setEditForm] = useState({
    event: '',
    org: '',
    city: '',
    venue: '',
    start: '',
    end: '',
    description: '',
    submittedBy: '',
    submitterEmail: ''
  });

  // Fetch pending events from Supabase
  useEffect(() => {
    fetchPendingEvents();
  }, []);

  const fetchPendingEvents = async () => {
    setLoading(true);
    try {
      // Fetch pending events from events table
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'pending')
        .order('submitted_at', { ascending: false });

      if (eventsError) throw eventsError;

      // Transform events data
      const transformedEvents = (eventsData || []).map(event => ({
        id: event.id,
        event: event.title,
        org: event.custom_organization || event.organization_id || 'Other',
        city: event.location || '',
        venue: event.venue_name || '',
        start: event.start_date,
        end: event.end_date,
        status: event.status,
        submittedBy: event.submitter_name || event.submitter_organization || 'Unknown',
        submittedAt: event.submitted_at || event.created_at,
        description: event.description || '',
        submitterEmail: event.submitter_email || '',
        website: event.website_url || '',
        source: 'events'
      }));

      // Also fetch speaker event submissions
      const { data: speakerSubmissions, error: speakerError } = await supabase
        .from('speaker_event_submissions')
        .select(`
          *,
          speakers (name)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (speakerError) {
        console.error('Error fetching speaker submissions:', speakerError);
      }

      // Transform speaker submissions
      const transformedSpeakerSubmissions = (speakerSubmissions || []).map(sub => ({
        id: sub.id,
        event: sub.event_name,
        org: 'Speaker Submission',
        city: sub.event_city || '',
        venue: sub.event_location || '',
        start: sub.event_date,
        end: sub.event_end_date,
        status: sub.status,
        submittedBy: sub.speakers?.name || 'Unknown Speaker',
        submittedAt: sub.created_at,
        description: sub.event_description || '',
        sessionTitle: sub.session_title || '',
        state: sub.event_state || '',
        source: 'speaker_submissions',
        speakerId: sub.speaker_id
      }));

      // Combine both sources
      const allPending = [...transformedEvents, ...transformedSpeakerSubmissions];
      setPendingEvents(allPending);
    } catch (error) {
      console.error('Error fetching pending events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (event, action) => {
    setSelectedEvent(event);
    setActionType(action);
    if (action === 'edit') {
      setEditForm({
        event: event.event,
        org: event.org,
        city: event.city,
        venue: event.venue,
        start: event.start,
        end: event.end,
        description: event.description,
        submittedBy: event.submittedBy,
        submitterEmail: event.submitterEmail || ''
      });
    }
    setShowModal(true);
  };

  const confirmAction = async () => {
    try {
      if (selectedEvent.source === 'speaker_submissions') {
        // Handle speaker event submissions
        if (actionType === 'approve') {
          // Create a new event from the speaker submission
          const { data: newEvent, error: createError } = await supabase
            .from('events')
            .insert({
              title: selectedEvent.event,
              description: selectedEvent.description,
              start_date: selectedEvent.start,
              end_date: selectedEvent.end,
              location: `${selectedEvent.city}${selectedEvent.state ? ', ' + selectedEvent.state : ''}`,
              venue_name: selectedEvent.venue,
              status: 'upcoming',
              submitter_name: selectedEvent.submittedBy
            })
            .select()
            .single();

          if (createError) throw createError;

          // Link the speaker to the event
          if (newEvent && selectedEvent.speakerId) {
            const { error: linkError } = await supabase.from('event_speakers').insert({
              event_id: newEvent.id,
              speaker_id: selectedEvent.speakerId
            });
            if (linkError) {
              console.error('Error linking speaker to event:', linkError);
            } else {
              console.log('Speaker linked to event successfully');
            }
          }

          // Update submission status to approved
          const { error } = await supabase
            .from('speaker_event_submissions')
            .update({ status: 'approved' })
            .eq('id', selectedEvent.id);
          
          if (error) throw error;
        } else if (actionType === 'reject') {
          // Update submission status to rejected
          const { error } = await supabase
            .from('speaker_event_submissions')
            .update({ status: 'rejected' })
            .eq('id', selectedEvent.id);
          
          if (error) throw error;
        }
        setPendingEvents(prev => prev.filter(e => e.id !== selectedEvent.id));
      } else {
        // Handle regular events
        if (actionType === 'approve') {
          const { error } = await supabase
            .from('events')
            .update({ status: 'upcoming' })
            .eq('id', selectedEvent.id);
          
          if (error) throw error;
          setPendingEvents(prev => prev.filter(e => e.id !== selectedEvent.id));
        } else if (actionType === 'reject') {
          const { error } = await supabase
            .from('events')
            .update({ status: 'cancelled' })
            .eq('id', selectedEvent.id);
          
          if (error) throw error;
          setPendingEvents(prev => prev.filter(e => e.id !== selectedEvent.id));
        }
      }
    } catch (error) {
      console.error('Error updating event status:', error);
      alert('Error: ' + error.message);
    }
    setShowModal(false);
    setSelectedEvent(null);
    setActionType(null);
  };

  const handleSaveEdit = async () => {
    try {
      // Update the event in Supabase
      const { error } = await supabase
        .from('events')
        .update({
          title: editForm.event,
          custom_organization: editForm.org,
          location: editForm.city,
          venue_name: editForm.venue,
          start_date: editForm.start,
          end_date: editForm.end,
          description: editForm.description,
          submitter_name: editForm.submittedBy,
          submitter_email: editForm.submitterEmail
        })
        .eq('id', selectedEvent.id);
      
      if (error) throw error;
      
      // Update local state
      setPendingEvents(prev => prev.map(e => 
        e.id === selectedEvent.id 
          ? { ...e, ...editForm }
          : e
      ));
    } catch (error) {
      console.error('Error saving event edits:', error);
    }
    setShowModal(false);
    setSelectedEvent(null);
    setActionType(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getOrgBadgeColor = (org) => {
    switch (org?.toLowerCase()) {
      case 'upci': return 'bg-purple-100 text-purple-800';
      case 'aljc': return 'bg-red-100 text-red-800';
      case 'aff': return 'bg-green-100 text-green-800';
      case 'speaker submission': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pending Events</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Review and approve event submissions from churches and organizations.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-2">
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
            {pendingEvents.length} pending
          </span>
        </div>
      </div>

      {/* Events Table */}
      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <svg className="animate-spin w-8 h-8 text-primary mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-500 dark:text-gray-400">Loading pending events...</p>
        </div>
      ) : pendingEvents.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Submitted By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {pendingEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{event.event}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{event.venue}, {event.city}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getOrgBadgeColor(event.org)}`}>
                        {event.org}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(event.start)}
                      {event.end && ` - ${formatDate(event.end)}`}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {event.submittedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(event.submittedAt)}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleAction(event, 'view')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                      <button
                        onClick={() => handleAction(event, 'edit')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleAction(event, 'approve')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Approve
                      </button>
                      <button
                        onClick={() => handleAction(event, 'reject')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">All caught up!</h3>
          <p className="text-gray-500 dark:text-gray-400">No pending events to review at this time.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowModal(false)} />
            
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full mx-auto shadow-xl transform transition-all">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {actionType === 'view' ? 'Event Details' : 
                     actionType === 'edit' ? 'Edit Event' :
                     actionType === 'approve' ? 'Approve Event' : 'Reject Event'}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4">
                {actionType === 'edit' ? (
                  /* Edit Form */
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Name</label>
                        <input
                          type="text"
                          value={editForm.event}
                          onChange={(e) => handleEditChange('event', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization</label>
                        <select
                          value={editForm.org}
                          onChange={(e) => handleEditChange('org', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                        >
                          <option value="UPCI">UPCI</option>
                          <option value="ALJC">ALJC</option>
                          <option value="AFF">AFF</option>
                          <option value="PAW">PAW</option>
                          <option value="WPF">WPF</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Venue</label>
                        <input
                          type="text"
                          value={editForm.venue}
                          onChange={(e) => handleEditChange('venue', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => handleEditChange('city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={editForm.start}
                          onChange={(e) => handleEditChange('start', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                        <input
                          type="date"
                          value={editForm.end}
                          onChange={(e) => handleEditChange('end', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                      <textarea
                        rows={3}
                        value={editForm.description}
                        onChange={(e) => handleEditChange('description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Submitted By</label>
                      <input
                        type="text"
                        value={editForm.submittedBy}
                        onChange={(e) => handleEditChange('submittedBy', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                ) : (
                  /* View/Approve/Reject Content */
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 dark:text-white">{selectedEvent.event}</h4>
                        <span className={`inline-flex mt-2 px-2 py-1 text-xs font-medium rounded-full ${getOrgBadgeColor(selectedEvent.org)}`}>
                          {selectedEvent.org}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Location</span>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedEvent.venue}</p>
                        <p className="text-gray-600 dark:text-gray-300">{selectedEvent.city}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Date</span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatDate(selectedEvent.start)} - {formatDate(selectedEvent.end)}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Submitted By</span>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedEvent.submittedBy}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Submitted On</span>
                        <p className="font-medium text-gray-900 dark:text-white">{formatDate(selectedEvent.submittedAt)}</p>
                      </div>
                    </div>

                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-sm">Description</span>
                      <p className="mt-1 text-gray-900 dark:text-white">{selectedEvent.description}</p>
                    </div>

                    {(actionType === 'approve' || actionType === 'reject') && (
                      <div className={`p-4 rounded-lg ${actionType === 'approve' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                        <p className={`text-sm ${actionType === 'approve' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                          {actionType === 'approve' 
                            ? 'This event will be published and visible to all users.'
                            : 'This event will be rejected and the submitter will be notified.'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  {actionType === 'view' ? 'Close' : 'Cancel'}
                </button>
                {actionType === 'view' ? (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleAction(selectedEvent, 'edit')}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setActionType('approve')}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => setActionType('reject')}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                ) : actionType === 'edit' ? (
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                  >
                    Save Changes
                  </button>
                ) : (
                  <button
                    onClick={confirmAction}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      actionType === 'approve' 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingEventsPage;
